import lang from '@/lang'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { cloneDeep, includes, uniqBy } from 'lodash'
import { processUploads } from '@/redux/ducks/document/uploadHelpers'
import { BatchItem } from '@rpldy/uploady'
import FileService from '@/modules/file/fileService'

export const DMS_UPLOAD_ALLOWED_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.xml', '.csv', '.iso', '.zip', '.rar', '.7z', '.tar', '.tar.gz', '.tar.bz2', '.tar.xz', '.mfb', '.json', '.txt', '.rtf', '.dvsdrw']
export const DMS_UPLOAD_MAX_SIZE = 100 * 1024 * 1024 * 1024

interface UploadQueueItem {
  checksum: string,
  uuid: string,
  scan: boolean,
  params,
  totalChunks: number,
  file: File,
  name: string,
  size: number,
  // FLAG:useDocuAi — selects the admission rule in processUploads and marks the resulting pending
  // row. Undefined for callers that predate this (treated as a legacy source).
  source?: string,
}
export interface PendingUpload {
  fileName: string,
  uploadToken: string,
  dmsToken?: string,
  checksum: string,
  status: string,
  maxPartNumber: number,
  totalChunks: number,
  id: string,
  uploadUrl: string,
  finished: boolean,
  dmsApiClient?: string,
  running?: boolean,
  pausing?: boolean,
  blocked?: boolean,
  file?: File,
  error?: string | object,
  fileId?: string,
  folderId?: string | number,
  analyze?: boolean,
  progress?: number
  source?: string,
  todoId?: string, // Add todo ID tracking
  // FLAG:useDocuAi — the batch uuid executeUpload assigned to this file. Lets classifyBatchUploads
  // join rows to batch entries precisely instead of by file name (two files named scan.pdf in one
  // drop used to share a row). Absent on rows restored by fetchPendingUploads, which have no batch.
  uuid?: string,
  // FLAG:useDocuAi — number of attempts made so far, surfaced in the flyout so an automatic retry
  // is visible rather than looking like a stall.
  attempt?: number,
  // FLAG:useDocuAi — bytes are in the DMS and complete-upload is running. Distinct from `finished`
  // so the flyout does not show the done actions while the last step is still in flight.
  completing?: boolean,
  // FLAG:useDocuAi — set once the object exists in the DMS. Its presence is what tells the flyout
  // retry to re-complete rather than re-upload (re-uploading would create a second DMS version).
  minioId?: string,
  maxAttempts?: number,
}

export interface ChecksumProgress {
  fileName: string,
  uuid: string,
  running?: boolean,
  currentChunk?: number,
  totalChunks?: number,
  error?: string,
  translatedError?: string,
  file?: File,
  params?,
  scan?: boolean,
  size?: number,
  // FLAG:useDocuAi — carried from the batch entry (enqueueUploadCalculations spreads it) so the
  // worker-error handler can tell a receipt upload from a legacy one.
  source?: string,
}

interface UploadError {
  message: string,
  meta: object,
  checksum?: string,
}

interface UploadDrawerState {
  open: boolean,
  files: BatchItem[],
  count: number,
}

type MimeTypeEntry = {
  mime_type: string;
  source: string | null;
  compressible: boolean | null;
  extensions: string[] | null;
  active: boolean;
};

export interface DocumentUploadState {
  allowedMimetypes: MimeTypeEntry[],
  pending: PendingUpload[],
  uploadQueue: UploadQueueItem[],
  calculating: ChecksumProgress[],
  checksumQueue: ChecksumProgress[],
  uploaderErrors: UploadError[],
  overlayOpen: boolean,
  drawer: UploadDrawerState,
  interval: NodeJS.Timeout,
  processing: boolean,
  todoUploads: Record<string, { // Add todo upload tracking
    todoId: string,
    pendingCount: number,
    totalCount: number,
    finishedCount: number,
    hasErrors: boolean,
  }>,
}

const initialState: DocumentUploadState = {
  allowedMimetypes: [],
  pending: [],
  calculating: [],
  checksumQueue: [],
  uploadQueue: [],
  overlayOpen: false,
  drawer: {
    open: false,
    files: [],
    count: 0,
  },
  uploaderErrors: [],
  interval: null,
  processing: false,
  todoUploads: {}, // Initialize todo upload tracking
}

export const fetchAllowedMimetypes = createAsyncThunk(
  'documentUpload/fetchAllowedMimetypes',
  async(_, thunkAPI) => {
    const allowedMimetypes = thunkAPI.getState().document.upload.allowedMimetypes
    if (allowedMimetypes.length) return allowedMimetypes

    try {
      const { data: { data: mimetypes } } = await FileService.getAllowedMimetypes()
      return mimetypes
    } catch {
      return []
    }
  },
)

// FLAG:useDocuAi — de-duplicate pending rows by uploadToken WITHOUT collapsing rows that have no
// token yet. Plain `uniqBy(rows, 'uploadToken')` treats every token-less row as the same key, so a
// second such row silently replaces the first and that file disappears from the batch entirely.
// Rows with a token dedupe exactly as before (the only case the legacy path ever produces, since
// prepare-upload rejects a missing token server-side), so flag-OFF behaviour is unchanged.
const dedupePendingRows = (rows: PendingUpload[]): PendingUpload[] => {
  const withToken = rows.filter(({ uploadToken }) => !!uploadToken)
  const withoutToken = rows.filter(({ uploadToken }) => !uploadToken)
  return [...uniqBy(withToken, 'uploadToken'), ...withoutToken]
}

const caclulateProcessingState = (state: DocumentUploadState) => {
  const pending = state.pending.filter(({ finished }) => !finished)
  const calculating = state.calculating
  state.processing = pending.length > 0 || calculating.length > 0
  return state
}

const documentUploadSlice = createSlice({
  name: 'documentUpload',
  initialState,
  reducers: {
    addPending(state, { payload }: PayloadAction<PendingUpload>) {
      return caclulateProcessingState({ ...state, pending: dedupePendingRows([...state.pending, payload]) })
    },
    setPending(state, { payload }: PayloadAction<PendingUpload[]>) {
      return caclulateProcessingState({ ...state, pending: dedupePendingRows([...state.pending, ...payload]) })
    },
    updatePending(state, { payload }: PayloadAction<PendingUpload>) {
      const i = state.pending.findIndex(({ uploadToken }) => payload.uploadToken === uploadToken)
      if (i === -1) {
        return state
      }
      const pending = [...state.pending]
      pending[i] = { ...state.pending[i], ...payload }
      return caclulateProcessingState({ ...state, pending: dedupePendingRows(pending) })
    },
    removePending(state, { payload }: PayloadAction<PendingUpload>) {
      return caclulateProcessingState({ ...state,
        pending: state.pending.filter(({ uploadToken }) => uploadToken !== payload.uploadToken) })
    },
    // FLAG:useDocuAi — drop the upload rows of documents that no longer exist. The list and the
    // uploader are separate slices with no link between them, so deleting a document from the grid
    // left its (failed) upload row in the flyout forever: nothing but the row's own ✕ ever called
    // removePending, and that ✕ can no longer succeed once the File row is gone — cancel-upload
    // answers NOT_FOUND. The row also keeps `processing` true, so the header counter stayed at
    // "0 of n complete" for uploads that cannot possibly finish.
    //
    // Compared as strings because the list carries numeric ids while the pending row carries
    // whatever prepare-upload returned. Rows without a fileId (nothing was created server-side yet)
    // are never matched and stay.
    removePendingByFileIds(state, { payload: fileIds }: PayloadAction<Array<string | number>>) {
      const removed = new Set((fileIds ?? []).map(String))
      const pending = state.pending.filter(({ fileId }) => !(fileId != null && removed.has(String(fileId))))
      if (pending.length === state.pending.length) {
        return state
      }
      return caclulateProcessingState({ ...state, pending })
    },
    addToUploadQueue(state, action: PayloadAction<UploadQueueItem>) {
      const interval = state.interval ?? setInterval(processUploads, 250)
      return caclulateProcessingState({ ...state, uploadQueue: [...state.uploadQueue, action.payload], interval })
    },
    removeFromUploadQueue(state, { payload }: PayloadAction<string>) {
      const uploadQueue = cloneDeep(state.uploadQueue)
      const i = uploadQueue.findIndex(({ uuid }) => uuid === payload)
      uploadQueue.splice(i, 1)
      return caclulateProcessingState({ ...state, uploadQueue })
    },
    addToChecksumQueue(state, action: PayloadAction<ChecksumProgress[]>) {
      const interval = state.interval ?? setInterval(processUploads, 250)
      return caclulateProcessingState({ ...state, checksumQueue: [...state.checksumQueue, ...action.payload], calculating: [...state.calculating, ...action.payload], interval })
    },
    removeFromChecksumQueue(state, { payload }: PayloadAction<string>) {
      const checksumQueue = cloneDeep(state.checksumQueue)
      const i = checksumQueue.findIndex(({ uuid }) => uuid === payload)
      checksumQueue.splice(i, 1)
      return caclulateProcessingState({ ...state, checksumQueue })
    },
    addChecksumCalculation(state, action: PayloadAction<ChecksumProgress>) {
      return caclulateProcessingState({ ...state, calculating: [...state.calculating, action.payload] })
    },
    updateChecksumCalculation(state, { payload }: PayloadAction<ChecksumProgress>) {
      const calculating = cloneDeep(state.calculating)
      const i = calculating.findIndex(({ uuid }) => uuid === payload.uuid)
      calculating[i].currentChunk = payload.currentChunk
      if (payload.running !== undefined) {
        calculating[i].running = payload.running
      }
      if (payload.error !== undefined) {
        calculating[i].error = payload.error
      }
      if (payload.translatedError) {
        calculating[i].translatedError = payload.translatedError
      }
      return { ...state, calculating }
    },
    removeChecksumCalculation(state, { payload }: PayloadAction<string>) {
      const calculating = cloneDeep(state.calculating)
      const i = calculating.findIndex(({ uuid }) => uuid === payload)
      calculating.splice(i, 1)
      return { ...state, calculating }
    },
    cancelUploads(state) {
      clearInterval(state.interval)
      return caclulateProcessingState({ ...initialState, allowedMimetypes: state.allowedMimetypes })
    },
    toggleUploadsOverlay(state, { payload: overlayOpen }: PayloadAction<boolean>) {
      const pending = overlayOpen
        ? state.pending
        : state.pending.filter(({ finished }) => !finished)
      return { ...state, overlayOpen, pending }
    },
    pushError(state, { payload: error }: PayloadAction<UploadError>) {
      const uploaderErrors = cloneDeep(state.uploaderErrors)
      if (!includes(uploaderErrors, error)) {
        uploaderErrors.push(error)
        toast.showToast({ type: 'error', message: lang.documents.uploadError })
      }
      return { ...state, uploaderErrors }
    },
    openUploadDrawer(state, { payload: count }: PayloadAction<number>) {
      return { ...state, drawer: { ...state.drawer, open: true, count }, overlayOpen: false }
    },
    addFiles(state, { payload: files = [] }: PayloadAction<BatchItem[]>) {
      const updatedCount = state?.drawer?.count || 0 + files.length
      const updatedFiles = [...state?.drawer?.files || [], ...files] as BatchItem[]
      return caclulateProcessingState({ ...state, processing: false, drawer: { open: true, count: updatedCount, files: updatedFiles } })
    },
    closeUploadDrawer(state) {
      return { ...state, drawer: initialState.drawer }
    },
    removeUploadFromDrawer(state, { payload: i }: PayloadAction<number>) {
      const files = [...state.drawer.files]
      files.splice(i, 1)
      return caclulateProcessingState({ ...state, drawer: { ...state.drawer, files, count: files.length } })
    },
    clearUploadInterval(state) {
      if (state.interval) clearInterval(state.interval)
      return { ...state, interval: null }
    },
    addTodoUpload(state, { payload: { todoId, totalCount } }: PayloadAction<{ todoId: string, totalCount: number }>) {
      state.todoUploads[todoId] = {
        todoId,
        pendingCount: totalCount,
        totalCount,
        finishedCount: 0,
        hasErrors: false,
      }
      return caclulateProcessingState({ ...state })
    },

    updateTodoUploadProgress(state, { payload: { todoId, finishedCount, hasErrors } }: PayloadAction<{ todoId: string, finishedCount: number, hasErrors?: boolean }>) {
      if (state.todoUploads[todoId]) {
        state.todoUploads[todoId].finishedCount = finishedCount
        state.todoUploads[todoId].pendingCount = state.todoUploads[todoId].totalCount - finishedCount
        if (hasErrors) {
          state.todoUploads[todoId].hasErrors = true
        }
      }
      return caclulateProcessingState({ ...state })
    },

    removeTodoUpload(state, { payload: todoId }: PayloadAction<string>) {
      delete state.todoUploads[todoId]
      return caclulateProcessingState({ ...state })
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchAllowedMimetypes.fulfilled, (state, action) => {
      state.allowedMimetypes = action.payload
    })
  },
})

export const {
  removeChecksumCalculation,
  updateChecksumCalculation,
  addToChecksumQueue,
  removeFromChecksumQueue,
  addToUploadQueue,
  removeFromUploadQueue,
  addChecksumCalculation,
  addPending,
  setPending,
  updatePending,
  removePending,
  removePendingByFileIds,
  cancelUploads,
  toggleUploadsOverlay,
  pushError,
  openUploadDrawer,
  addFiles,
  closeUploadDrawer,
  removeUploadFromDrawer,
  clearUploadInterval,
  addTodoUpload,
  updateTodoUploadProgress,
  removeTodoUpload,
} = documentUploadSlice.actions
export default documentUploadSlice.reducer
